import moment from 'moment';
import { formatNumberToKNotation } from '../../../../helpers';
import { GenericDropdownOption } from '../../../../App.types';
import { creatorbase, influencer } from '@round/api';

export const platformOptions: GenericDropdownOption<Extract<creatorbase.Platform, 'tiktok' | 'instagram'>>[] = [
    { value: 'tiktok', label: 'TikTok' },
    { value: 'instagram', label: 'Instagram' },
];

export const formatPercentage = (value: number | null | undefined) => {
    return typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : '-';
};

export const formatNumber = (value: number | null | undefined) => {
    return typeof value === 'number' ? formatNumberToKNotation(value) : '-';
};

export type TableRow = influencer.CreatorSearchResult & {
    plans?: any[];
    created?: string;
    influencer_tags?: string[];
    userImage?: string | null;
};

export type AverageDraftTimeOption = GenericDropdownOption<number>;

// draft time from API is in hours
export const maxAverageDraftTimeOptions: AverageDraftTimeOption[] = [
    {
        label: 'Within 2 days',
        value: 48,
    },
    {
        label: 'Within 4 days',
        value: 96,
    },
    {
        label: 'Within 7 days',
        value: 168,
    },
];

export const daysAgo = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
};

export type LastUsedTimeOption = GenericDropdownOption<string> & { days: number };

const LAST_USED_DAYS = [30, 90, 180, 365] as const;
const LAST_USED_LABELS: Record<(typeof LAST_USED_DAYS)[number], string> = {
    30: 'Last 1 month',
    90: 'Last 3 months',
    180: 'Last 6 months',
    365: 'Last 12 months',
};

// Options are recalculated each time, so "Last 30 days" is always from today
export const getMaxLastUsedTimeOptions = (): LastUsedTimeOption[] =>
    LAST_USED_DAYS.map((days) => ({
        label: LAST_USED_LABELS[days],
        value: daysAgo(days),
        days,
    }));

// Get option for a given days value with value recalculated from today
// (for restoring persisted "last used" choice).
export const getLastUsedOptionForDays = (days: number): LastUsedTimeOption | undefined => {
    const label = LAST_USED_LABELS[days as (typeof LAST_USED_DAYS)[number]];
    if (!label) return undefined;
    return { label, value: daysAgo(days), days };
};

// Resolve a stored date (e.g., from URL) to the matching "last used" option with value recalculated from today.
export const getLastUsedOptionFromStoredDate = (storedDateIso: string | undefined): LastUsedTimeOption | undefined => {
    if (!storedDateIso) return undefined;
    const daysDiff = Math.round((Date.now() - new Date(storedDateIso).getTime()) / (24 * 60 * 60 * 1000));
    const options = getMaxLastUsedTimeOptions();
    return options.reduce((best, opt) => (Math.abs(opt.days - daysDiff) < Math.abs(best.days - daysDiff) ? opt : best));
};

// The Creator Explore page seeds this into its URL state so the filter opens on 12 months instead
// of "Any". Scoped to that page on purpose: the pool drawer and the add-to-campaign modal keep
// opening unfiltered.
export const LAST_BOOKED_OR_ADDED_DEFAULT_DAYS = 365;

/** Matches the old dashboard's "New" badge window. */
export const NEW_CREATOR_WINDOW_DAYS = 30;

export const isRecentlyAddedCreator = (firstSeen: string | null | undefined): boolean => {
    if (!firstSeen) {
        return false;
    }
    const addedAt = new Date(firstSeen).getTime();
    return Number.isFinite(addedAt) && addedAt > Date.now() - NEW_CREATOR_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

export const getAverageDraftTimeLabel = (draftTimeInHours: number | null | undefined): string => {
    if (typeof draftTimeInHours !== 'number') {
        return '-';
    }

    const averageDraftTimeBuckets = [
        {
            label: 'Within 2 days',
            value: [0, 48],
        },
        {
            label: '2-4 days',
            value: [49, 96],
        },
        {
            label: '4-7 days',
            value: [97, 168],
        },
        {
            label: 'Over a week',
            value: [169, Infinity],
        },
    ];

    const matchingOption = averageDraftTimeBuckets.find((bucket) => {
        const [min, max] = bucket.value;
        return draftTimeInHours >= min && draftTimeInHours <= max;
    });

    return matchingOption?.label || '-';
};

export const getLastCollabLabel = (dateString: string | null | undefined): string => {
    if (!dateString) {
        return '-';
    }
    const date = moment(dateString);
    if (!date.isValid()) {
        return '-';
    }
    const today = moment().startOf('day');
    if (date.isSame(today, 'day')) {
        return 'Today';
    }
    return date.fromNow();
};

export type PostingFrequencyTier =
    | 'extremely_active'
    | 'very_active'
    | 'active'
    | 'inactive'
    | 'very_inactive'
    | 'insufficient_data';

export type PostingFrequencyOption = GenericDropdownOption<PostingFrequencyTier>;

// Matches PostingFrequencyTier's labels/values in app/creatorbase/constants.py -- keep in sync.
export const postingFrequencyOptions: PostingFrequencyOption[] = [
    { value: 'extremely_active', label: 'Extremely active' },
    { value: 'very_active', label: 'Very active' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'very_inactive', label: 'Very inactive' },
    { value: 'insufficient_data', label: 'Insufficient data' },
];

const POSTING_FREQUENCY_TIER_LABELS: Record<Exclude<PostingFrequencyTier, 'insufficient_data'>, string> = {
    extremely_active: 'Extremely active',
    very_active: 'Very active',
    active: 'Active',
    inactive: 'Inactive',
    very_inactive: 'Very inactive',
};

/**
 * Buckets a per-platform avg_posts_per_day into one of the five active tiers.
 * Mirrors POSTING_FREQUENCY_TIER_BOUNDS in app/creatorbase/constants.py exactly, so the FE
 * and BE never disagree on where a creator falls. Returns undefined for null/missing --
 * that's a "no data for this platform" cell, not the (cross-platform, filter-only)
 * insufficient_data tier.
 */
export const getPostingFrequencyTier = (
    avgPostsPerDay: number | null | undefined
): Exclude<PostingFrequencyTier, 'insufficient_data'> | undefined => {
    if (typeof avgPostsPerDay !== 'number') {
        return undefined;
    }
    if (avgPostsPerDay >= 1 / 3) {
        return 'extremely_active';
    }
    if (avgPostsPerDay >= 1 / 7) {
        return 'very_active';
    }
    if (avgPostsPerDay >= 1 / 14) {
        return 'active';
    }
    if (avgPostsPerDay >= 1 / 30) {
        return 'inactive';
    }
    return 'very_inactive';
};

export const getPostingFrequencyLabel = (avgPostsPerDay: number | null | undefined): string => {
    const tier = getPostingFrequencyTier(avgPostsPerDay);
    return tier ? POSTING_FREQUENCY_TIER_LABELS[tier] : '-';
};

const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;

const pluralize = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'}`;

// Keeps very inactive creators' day gap (e.g. 450) from reading as a raw number of days.
const formatDayGapSpan = (days: number): string => {
    if (days < DAYS_PER_MONTH) {
        return pluralize(days, 'day');
    }
    if (days < DAYS_PER_YEAR) {
        return pluralize(Math.round(days / DAYS_PER_MONTH), 'month');
    }
    return pluralize(1, 'year');
};

/** Hover text for the underlying cadence, e.g. "Posts about every 9 days" or "Posts more than a year apart". Undefined when there's no rate to show. */
export const getPostingFrequencyDayGapLabel = (avgPostsPerDay: number | null | undefined): string | undefined => {
    if (typeof avgPostsPerDay !== 'number' || avgPostsPerDay <= 0) {
        return undefined;
    }
    const daysBetweenPosts = Math.round(1 / avgPostsPerDay);
    if (daysBetweenPosts <= 1) {
        return 'Posts about once a day';
    }
    // Beyond a year the exact count isn't meaningful, so cap it instead of showing e.g. "2 years".
    if (Math.round(daysBetweenPosts / DAYS_PER_YEAR) >= 2) {
        return 'Posts more than a year apart';
    }
    return `Posts about once every ${formatDayGapSpan(daysBetweenPosts)}`;
};

/** Validates a raw postingFrequency URL param against the known tiers. Undefined for anything else. */
export const getPostingFrequencyTierFromUrlState = (value: string | undefined): PostingFrequencyTier | undefined => {
    if (!value) {
        return undefined;
    }
    const isKnownTier = postingFrequencyOptions.some((option) => option.value === value);
    return isKnownTier ? (value as PostingFrequencyTier) : undefined;
};

export function getPlatformCost(
    clientInfluencer: creatorbase.ClientInfluencer | undefined | null,
    platform: 'tiktok' | 'instagram'
): { cost: number | null; currencyId: number | null } {
    if (!clientInfluencer) {
        return { cost: null, currencyId: null };
    }

    return {
        cost: clientInfluencer[`${platform}_cost`],
        currencyId: clientInfluencer[`${platform}_currency_id`],
    };
}
